/**
 * DocumentVerifyPanel — the "Direct" recording-style panel for a Phase-1
 * category step in the Full Audit Wizard. Wraps the Verification Queue:
 *   - filter chips (All / Unverified / Verified / Missing-proof)
 *   - search by PO# / DR# / invoice# / party
 *   - progress bar (verified / total)
 *   - each row → opens the 3-way compare modal (encoded | photo | phone QR)
 * Reuses GET /api/audit-wizard/verification-queue and routes/verify.py.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../contexts/AuthContext';
import { formatPHP } from '../../../lib/utils';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import {
  Search, ShieldCheck, ImageOff, CheckCircle2, AlertTriangle, Loader2, FileSearch,
  ChevronDown, ChevronRight, ExternalLink,
} from 'lucide-react';
import ThreeWayCompareModal from '../ThreeWayCompareModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Wizard step category → Verification-Queue category (verify.py COLLECTION_MAP)
const CATEGORY_MAP = {
  purchase_orders: 'purchase_order',
  expenses: 'expense',
  transfers: 'branch_transfer',
};

// Edit page routes for "Open to edit" links
const EDIT_ROUTES = {
  purchase_order: '/purchase-orders',
  expense: '/expenses',
  branch_transfer: '/transfers',
};

export default function DocumentVerifyPanel({ session, stepCategory }) {
  const vqCategory = CATEGORY_MAP[stepCategory];
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, verified: 0, unverified: 0, missing_proof: 0, with_discrepancy: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showVerified, setShowVerified] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    if (!vqCategory) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ category: vqCategory, status: 'all' });
      if (session?.branch_id) params.set('branch_id', session.branch_id);
      if (session?.period_from) params.set('period_from', session.period_from);
      if (session?.period_to) params.set('period_to', session.period_to);
      if (search.trim()) params.set('search', search.trim());
      const res = await api.get(`${BACKEND_URL}/api/audit-wizard/verification-queue?${params}`);
      setItems(res.data.items || []);
      setSummary(res.data.summary || {});
    } catch { setItems([]); }
    setLoading(false);
  }, [vqCategory, search, session?.branch_id, session?.period_from, session?.period_to]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0); // debounce search
    return () => clearTimeout(t);
  }, [load, search]);

  const pct = summary.total > 0 ? Math.round((summary.verified / summary.total) * 100) : 0;
  const unverifiedItems = items.filter(it => !it.verified);
  const verifiedItems = items.filter(it => it.verified);
  const editRoute = EDIT_ROUTES[vqCategory] || null;

  if (!vqCategory) {
    return <p className="text-sm text-slate-400 py-8 text-center">This category has no document queue.</p>;
  }

  const DocRow = ({ it }) => (
    <div key={it.id} data-testid={`verify-row-${it.id}`}
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-[#1A4D2E]/30 transition-colors">
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
        it.verified ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
        {it.verified ? <CheckCircle2 size={16} /> : <ShieldCheck size={16} />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800 truncate">
          {it.label}
          {it.ref && it.ref !== it.label && <span className="text-slate-400 font-normal"> · {it.ref}</span>}
        </p>
        <p className="text-xs text-slate-400 truncate">
          {it.party && <span>{it.party} · </span>}{it.date} · {formatPHP(it.total)}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {it.has_discrepancy && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">&#916;</span>}
        {it.missing_proof && <span title="No proof attached"><ImageOff size={14} className="text-amber-500" /></span>}
        {editRoute && (
          <a href={`${editRoute}?id=${it.id}`} target="_blank" rel="noopener noreferrer"
            title="Open to edit" data-testid={`verify-edit-link-${it.id}`}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-300 hover:text-[#1A4D2E] hover:bg-[#1A4D2E]/10 transition-colors">
            <ExternalLink size={13} />
          </a>
        )}
        <Button size="sm" variant={it.verified ? 'outline' : 'default'}
          className={it.verified ? '' : 'bg-[#1A4D2E] hover:bg-[#14532d] text-white'}
          onClick={() => setActive(it)} data-testid={`verify-open-compare-${it.id}`}>
          {it.verified ? 'Review' : 'Verify'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="document-verify-panel">
      {/* Progress */}
      <div className="rounded-lg border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-slate-600">{summary.verified} verified / {summary.total}</span>
          <div className="flex items-center gap-3 text-[11px]">
            {summary.missing_proof > 0 && <span className="text-amber-600 flex items-center gap-1"><ImageOff size={12} />{summary.missing_proof} missing proof</span>}
            {summary.with_discrepancy > 0 && <span className="text-red-600 flex items-center gap-1"><AlertTriangle size={12} />{summary.with_discrepancy} discrepancy</span>}
          </div>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-[#1A4D2E] transition-all" style={{ width: `${pct}%` }} data-testid="verify-progress-bar" />
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search PO# / DR# / invoice# / party"
          className="pl-8 h-9 text-sm" data-testid="verify-queue-search" />
      </div>

      {/* Unverified list (default view) */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>
      ) : unverifiedItems.length === 0 && verifiedItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-slate-400">
          <FileSearch size={28} className="mb-2 opacity-40" />
          <p className="text-sm">No documents in this period.</p>
        </div>
      ) : (
        <>
          {unverifiedItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
              <CheckCircle2 size={24} className="mb-2 text-emerald-500 opacity-80" />
              <p className="text-sm text-emerald-700 font-semibold">All documents verified!</p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1" data-testid="unverified-list">
              {unverifiedItems.map(it => <DocRow key={it.id} it={it} />)}
            </div>
          )}

          {/* Verified collapse toggle */}
          {verifiedItems.length > 0 && (
            <div>
              <button onClick={() => setShowVerified(v => !v)} data-testid="toggle-show-verified"
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors py-1">
                {showVerified ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                Show verified ({verifiedItems.length})
              </button>
              {showVerified && (
                <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1 mt-1.5" data-testid="verified-list">
                  {verifiedItems.map(it => <DocRow key={it.id} it={it} />)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <ThreeWayCompareModal
        open={!!active}
        item={active}
        onClose={() => setActive(null)}
        onVerified={() => { setActive(null); load(); }}
      />
    </div>
  );
}
